/**
 * "Show results in" — publish a pipeline's run results to a Solution page.
 *
 * Business users cannot work with the canvas but want the results; a page
 * such as Clinical → eTMF intake shows the runs of every pipeline published to
 * it, read-only, to every role (ADR-0032). This dialog is the only place the
 * pipeline world and the solution world meet.
 *
 * Developer+ and the owner (or an `edit` grantee) server-side; the canvas only
 * offers it to Developer+. Production is recommended, not required — the
 * dialog says so rather than refusing, and the page shows each run's
 * lifecycle state.
 */

import { useEffect, useState } from "react";
import { Alert, Button, Group, Modal, Select, Stack, Text } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { listSolutionPages, type SolutionPageOut } from "@/shared/api/solutionPages";
import { toastApiError, toastSuccess } from "@/shared/ui/toast";

/** The select's value for "not published". Mantine's Select cannot hold null
 *  as an option value. */
const NONE = "__none__";

interface Props {
  opened: boolean;
  onClose: () => void;
  /** The page it is published to now, or null. */
  current: string | null;
  lifecycle: string;
  /** Performs the change; resolves once saved. */
  onSubmit: (solutionPage: string | null) => Promise<void>;
}

export function PublishToSolutionModal({ opened, onClose, current, lifecycle, onSubmit }: Props) {
  const getToken = useAccessToken();
  const [pages, setPages] = useState<SolutionPageOut[]>([]);
  const [value, setValue] = useState<string>(current ?? NONE);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (opened) setValue(current ?? NONE);
  }, [opened, current]);

  useEffect(() => {
    if (!opened) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await listSolutionPages(await getToken());
        if (!cancelled) setPages(res.items);
      } catch (err) {
        if (!cancelled) toastApiError("Could not load the Solution pages", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [opened, getToken]);

  const archived = lifecycle === "archived";
  const next = value === NONE ? null : value;
  const label = (id: string | null) => pages.find((p) => p.id === id)?.label ?? id;

  const submit = async () => {
    setSaving(true);
    try {
      await onSubmit(next);
      toastSuccess(
        next ? "Results published" : "Results no longer published",
        next
          ? `This pipeline's runs now appear on ${label(next)}.`
          : "Its runs no longer appear on a Solution page.",
      );
      onClose();
    } catch (err) {
      toastApiError("Could not change where results are shown", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Show results in" centered>
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          The runs of this pipeline — what happened, to which documents — appear on the chosen
          Solution page for everyone who can open it. Results only: nobody there sees the canvas or
          the configuration.
        </Text>
        <Select
          label="Solution page"
          data-autofocus
          allowDeselect={false}
          disabled={archived}
          value={value}
          onChange={(v) => setValue(v ?? NONE)}
          data={[
            { value: NONE, label: "Not published" },
            ...pages.map((p) => ({ value: p.id, label: p.label })),
          ]}
        />
        {archived ? (
          <Alert color="gray" icon={<IconInfoCircle size={16} />}>
            An archived pipeline's results are not published.
          </Alert>
        ) : (
          next &&
          lifecycle !== "production" && (
            <Alert color="yellow" icon={<IconInfoCircle size={16} />}>
              This pipeline is in {lifecycle}. Publishing is allowed, but Production is recommended
              — the page labels each run with the state it ran in.
            </Alert>
          )
        )}
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            loading={saving}
            disabled={archived || next === current}
          >
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
